// Federación de Pádel — the diegetic layer that ties club growth to
// competitive standing (the "Licencia Federativa"). Two mechanisms:
//
// HARD GATES — high facility levels declare `leagueDivision` requirements in
// the facility registry; this module names those gates (licenses) and builds
// the Spanish lock messaging.
//
// SOFT SCALING — sponsors and demand scale with the club's division, so an
// over-built club stuck in Cuarta is possible but unprofitable. All
// multipliers are 1.0 for "no league" and Cuarta: identity for new games and
// the deterministic test suite.

import { LOWEST_DIVISION, type LeagueState } from './leagueConfig'

/** Sentinel division for clubs not enrolled in any league: ranks below
 *  Cuarta (higher number = worse). */
export const NO_LEAGUE_DIVISION = LOWEST_DIVISION + 1

export interface FederationLicense {
  id: 'municipal' | 'regional' | 'autonomica' | 'nacional' | 'elite'
  name: string
  /** Minimum division this license requires (4..1); null = none needed */
  minDivision: number | null
  blurb: string
}

export const FEDERATION_LICENSES: FederationLicense[] = [
  {
    id: 'municipal',
    name: 'Licencia Municipal',
    minDivision: null,
    blurb: 'Un club de barrio sin competición federada.',
  },
  {
    id: 'regional',
    name: 'Licencia Regional',
    minDivision: 4,
    blurb: 'Inscrito en la liga regional de clubes.',
  },
  {
    id: 'autonomica',
    name: 'Licencia Autonómica',
    minDivision: 3,
    blurb: 'La Federación autoriza ampliaciones de categoría autonómica.',
  },
  {
    id: 'nacional',
    name: 'Licencia Nacional',
    minDivision: 2,
    blurb: 'Instalaciones homologadas para competición nacional.',
  },
  {
    id: 'elite',
    name: 'Licencia de Élite',
    minDivision: 1,
    blurb: 'El máximo reconocimiento federativo: Primera División.',
  },
]

const DIVISION_ORDINALS: Record<number, string> = {
  4: '4ª División',
  3: '3ª División',
  2: '2ª División',
  1: '1ª División',
}

/** Current effective division: the league's, or the "sin liga" sentinel. */
export function getDivisionOrNone(league: LeagueState | null): number {
  return league?.division ?? NO_LEAGUE_DIVISION
}

/** Divisions are inverted (1 is best): current qualifies if <= required. */
export function meetsDivision(currentDivision: number, minDivision: number): boolean {
  return currentDivision <= minDivision
}

/** The license that authorizes a given minimum division. */
export function licenseForDivision(minDivision: number): FederationLicense {
  return FEDERATION_LICENSES.find(l => l.minDivision === minDivision) ?? FEDERATION_LICENSES[0]
}

/** Short lock label: "Requiere 2ª División" */
export function divisionGateLabel(minDivision: number): string {
  return `Requiere ${DIVISION_ORDINALS[minDivision] ?? `División ${minDivision}`}`
}

/** Long diegetic explanation for tooltips/hints. */
export function divisionGateMessage(minDivision: number): string {
  const license = licenseForDivision(minDivision)
  return `${license.name} insuficiente: la Federación solo autoriza esta ampliación a clubes de ` +
    `${DIVISION_ORDINALS[minDivision] ?? `División ${minDivision}`} o superior. Asciende en la liga para desbloquearla.`
}

// --- Court license gates -------------------------------------------------------
// Expanding beyond a neighborhood-club footprint needs federation approval.
// A LADDER instead of a single wall so growth never stalls for a whole season:
// courts 7-8 need Segunda, court 9+ needs Primera (aligned with club levels
// 7/9, which ask for 8/12 courts); the 3rd indoor court requires Segunda.

export const COURT_GATES = {
  totalCourts: [
    { threshold: 7, minDivision: 2 },
    { threshold: 9, minDivision: 1 },
  ],
  indoorCourts: [{ threshold: 3, minDivision: 2 }],
} as const

/** Strictest gate the NEXT court trips, given how many exist already. */
function ladderGate(
  gates: readonly { threshold: number; minDivision: number }[],
  current: number,
): number | null {
  let required: number | null = null
  for (const gate of gates) {
    if (current + 1 >= gate.threshold && (required === null || gate.minDivision < required)) {
      required = gate.minDivision
    }
  }
  return required
}

/** Minimum division required to build the NEXT court of this type given the
 *  current counts (built + queued), or null when ungated. */
export function courtLicenseGate(
  totalCourts: number,
  indoorCourts: number,
  type: 'outdoor' | 'indoor',
): number | null {
  const totalGate = ladderGate(COURT_GATES.totalCourts, totalCourts)
  const indoorGate = type === 'indoor' ? ladderGate(COURT_GATES.indoorCourts, indoorCourts) : null
  if (totalGate === null) return indoorGate
  if (indoorGate === null) return totalGate
  return Math.min(totalGate, indoorGate) // lower number = higher division = stricter
}

// --- Soft economic scaling ---------------------------------------------------

/** Sponsor monthly payments scale with division prestige (identity ≤ Cuarta) */
const DIVISION_SPONSOR_MULT: Record<number, number> = {
  [NO_LEAGUE_DIVISION]: 1.0,
  4: 1.0,
  3: 1.3,
  2: 1.7,
  1: 2.2,
}

/** Demand grows with league prestige (identity ≤ Cuarta) */
const DIVISION_DEMAND_MULT: Record<number, number> = {
  [NO_LEAGUE_DIVISION]: 1.0,
  4: 1.0,
  3: 1.05,
  2: 1.1,
  1: 1.15,
}

export function getDivisionSponsorMult(division: number): number {
  return DIVISION_SPONSOR_MULT[division] ?? 1.0
}

export function getDivisionDemandMult(division: number): number {
  return DIVISION_DEMAND_MULT[division] ?? 1.0
}
